import { useMemo } from "react";
import { createListCollection } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import Select from "@/components/select/select";
import {
  DEFAULT_STATIC_OVERLAY_COLOR,
  isStaticOverlayColor,
  resolveStoredColorToOverlayColor,
  STATIC_OVERLAY_COLOR_KEYS,
  type StaticOverlayColor,
} from "@/features/shared/widgets/model-map/static-overlay-color-presets";

import styles from "./static-overlay-color-select.module.css";

export interface StaticOverlayColorSelectProps {
  /** Raw `color` from configuration (enum string or invalid legacy value). */
  value: string;
  onValueChange: (color: StaticOverlayColor) => void;
  /** Omit when the parent provides context (e.g. static overlay card next to a swatch). */
  label?: string;
  /** Shown when the saved value is not a valid enum member. */
  showUnknownHint?: boolean;
}

export function StaticOverlayColorSelect({
  value,
  onValueChange,
  label,
  showUnknownHint = true,
}: StaticOverlayColorSelectProps) {
  const { t, i18n } = useTranslation();

  const { color: selectedColor, isUnknown } = resolveStoredColorToOverlayColor(value);

  const collection = useMemo(
    () =>
      createListCollection({
        items: STATIC_OVERLAY_COLOR_KEYS.map((k) => ({
          label: t(`admin.widgets.fields.model_map.static_overlays.presets.${k}`),
          value: k,
        })),
      }),
    [t, i18n.language],
  );

  const handleChange = (v: string | undefined) => {
    const next = v ?? DEFAULT_STATIC_OVERLAY_COLOR;
    if (isStaticOverlayColor(next)) onValueChange(next);
  };

  return (
    <div className={styles.wrap}>
      <Select
        label={label}
        collection={collection}
        value={selectedColor}
        onValueChange={handleChange}
        placeholder={t(
          `admin.widgets.fields.model_map.static_overlays.presets.${DEFAULT_STATIC_OVERLAY_COLOR}`,
        )}
      />
      {showUnknownHint && isUnknown ? (
        <p className={styles.unknownHint}>
          {t("admin.widgets.fields.model_map.static_overlays.unknown_color_hint")}
        </p>
      ) : null}
    </div>
  );
}
